import CustomApplicationState from "@crescentParts/Store/CustomApplicationState";
import requestConvertQuoteSendEmail from "@crescentParts/Store/Data/RequestConvertQuote/Handler/RequestCovertQuoteHandler";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import siteMessage from "@insite/client-framework/SiteMessage";
import shareEntity from "@insite/client-framework/Store/CommonHandlers/ShareEntity";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { ShareEntityModel } from "@insite/client-framework/Types/ApiModels";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Select, { SelectProps } from "@insite/mobius/Select";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    entityId?: string;
    entityName?: "Order" | "Invoice";
    extendedStyles?: RequestConvertQuoteButtonStyles;
    variant?: "button" | "clickable";
    /**
     * If true, the email attachment will be generated using the entity details web page, rather than a pre-defined PDF file.
     */
    generateAttachmentFromWebpage?: boolean;
    extraProperties?: SafeDictionary<string>;
}

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        location: getLocation(state),
        requestConvertQuoteState: state.cpeState.RequestConvertQuote,
    };
};
const mapDispatchToProps = {
    shareEntity,
    requestConvertQuoteSendEmail,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RequestConvertQuoteButtonStyles {
    clickable?: ClickablePresentationProps;
    button?: ButtonPresentationProps;
    modal?: ModalPresentationProps;
    container?: GridContainerProps;
    messageGridItem?: GridItemProps;
    messageTextArea?: TextAreaProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    sendButton?: ButtonPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    select?: SelectProps;
    date?: DatePickerPresentationProps;
}

interface RCQStyles {
    purchaseOrderTextField?: TextFieldPresentationProps;
}
export const rcqStyles: RCQStyles = {};
export const requestConvertQuoteButtonStyles: RequestConvertQuoteButtonStyles = {
    button: {
        color: "secondary",
    },
    modal: {
        size: 600,
    },
    messageGridItem: {
        width: 12,
    },
    buttonsWrapper: {
        css: css`
            margin-top: 30px;
            text-align: right;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    sendButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    date: {
        cssOverrides: {
            inputSelect: css`
                width: 100%;
            `,
            formField: css`
                padding-right: 5px;
            `,
        },
    },
};

const dropDownOption = ["Customer Pickup", "Delivery", "UPS-Ground", "UPS-Next Day"];
const RequestConvertQuoteButton = ({
    extendedStyles,
    variant = "button",
    location,
    requestConvertQuoteState,
    requestConvertQuoteSendEmail,
}: Props) => {
    const queryParams: any = parseQueryString(location.search);
    const [styles] = useState(() => mergeToNew(requestConvertQuoteButtonStyles, extendedStyles));
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [deliveryMethod, setDeliveryMethod] = useState("");
    const [date, setDate] = useState<any>();
    const [purchaseOrderNumber, setPurchaseOrderNumber] = useState("");
    const [purchaseOrderNumberError, setPurchaseOrderNumberError] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const toasterContext = useContext(ToasterContext);

    const messageChangeHandler = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setMessage(event.target.value);
    };

    const deliveryMethodHandleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setDeliveryMethod(event.target.value);
    };

    const dateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        setDate(selectedDay);
    };

    const purchaseOrderNumberChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPurchaseOrderNumber(event.target.value);
        setIsSubmitted(false);
        setPurchaseOrderNumberError("");
    };

    const modalCloseHandler = () => {
        setModalIsOpen(false);
        setIsSubmitted(false);
        setPurchaseOrderNumberError("");
    };

    const requestConvertQuoteButtonClick = () => {
        setModalIsOpen(true);
    };
    const sendButtonClickHandler = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const purchaseFieldError = purchaseOrderNumber === "";
        setIsSubmitted(true);
        setPurchaseOrderNumberError(purchaseFieldError ? "Purchase Order Number is required" : "");

        if (purchaseFieldError) {
            return false;
        }

        requestConvertQuoteSendEmail({
            Message: message,
            OrderNumber: queryParams.orderNumber,
            PurchaseOrderNumber: purchaseOrderNumber,
            DeliveryMethod: deliveryMethod,
            DateNeeded: date,
            onSuccess: () => {
                setModalIsOpen(false);
                setIsSubmitted(false);
                setMessage("");
                setPurchaseOrderNumber("");
                setPurchaseOrderNumberError("");
                setDeliveryMethod("");
                setDate(undefined);
                toasterContext.addToast({
                    body: translate("Request has been sent successfully!"),
                    messageType: "success",
                });
            },
        });
    };

    return (
        <>
            {variant === "clickable" && (
                <Clickable
                    {...styles.clickable}
                    onClick={requestConvertQuoteButtonClick}
                    data-test-selector="requestConvertQuote_email"
                >
                    {translate("Request to Convert Quote")}
                </Clickable>
            )}
            {variant === "button" && (
                <Button
                    {...styles.button}
                    onClick={requestConvertQuoteButtonClick}
                    data-test-selector="requestConvertQuote_email"
                >
                    {translate("Request to Convert Quote")}
                </Button>
            )}
            <Modal
                {...styles.modal}
                headline={translate("Request Quote Conversion")}
                isOpen={modalIsOpen}
                handleClose={modalCloseHandler}
            >
                <form id="rcqform" onSubmit={sendButtonClickHandler} noValidate>
                    {requestConvertQuoteState.isEmalSending ? (
                        <StyledWrapper {...styles.centeringWrapper}>
                            <LoadingSpinner {...styles.spinner} />
                        </StyledWrapper>
                    ) : (
                        <GridContainer {...styles.container} data-test-selector="requestConvertQuote_modal">
                            <GridItem {...styles.messageGridItem}>
                                <TextField
                                    label={translate("Purchase Order Number")}
                                    value={purchaseOrderNumber}
                                    onChange={purchaseOrderNumberChangeHandler}
                                    data-test-selector="requestConvertQuote_message"
                                    {...rcqStyles.purchaseOrderTextField}
                                    required
                                    error={isSubmitted && purchaseOrderNumberError}
                                />
                            </GridItem>
                            <GridItem {...styles.messageGridItem}>
                                <TextArea
                                    label={translate("Additional Comments")}
                                    value={message}
                                    onChange={messageChangeHandler}
                                    data-test-selector="requestConvertQuote_message"
                                    {...styles.messageTextArea}
                                />
                            </GridItem>
                            <GridItem {...styles.messageGridItem}>
                                <Select
                                    data-test-selector="requestConvertQuote_delivery_method"
                                    label={translate("Delivery Method")}
                                    {...styles.select}
                                    value={deliveryMethod}
                                    onChange={deliveryMethodHandleChange}
                                >
                                    <option value="">{translate("Select Delivery Method")}</option>
                                    {dropDownOption.map(operator => (
                                        <option key={operator} value={operator}>
                                            {}
                                            {translate(operator)}
                                        </option>
                                    ))}
                                </Select>
                            </GridItem>
                            <GridItem {...styles.messageGridItem}>
                                <DatePicker
                                    data-test-selector="requestConvertQuote_date"
                                    {...styles.date}
                                    label={translate("Date Needed")}
                                    selectedDay={date}
                                    onDayChange={dateChangeHandler}
                                    dateTimePickerProps={{
                                        clearIcon: null,
                                        ...styles.date?.dateTimePickerProps,
                                    }}
                                />
                            </GridItem>
                        </GridContainer>
                    )}
                    <StyledWrapper {...styles.buttonsWrapper}>
                        <Button
                            {...styles.cancelButton}
                            onClick={modalCloseHandler}
                            data-test-selector="requestConvertQuote_cancel"
                        >
                            {translate("Cancel")}
                        </Button>
                        <Button {...styles.sendButton} data-test-selector="requestConvertQuote_submit">
                            {translate("Send")}
                        </Button>
                    </StyledWrapper>
                </form>
            </Modal>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RequestConvertQuoteButton);
